import type { ReactElement } from "react";
import { Link, Route, Routes } from "react-router";

import { PageHeading } from "@/components/PageHeading";
import { SaveBarProvider } from "@/components/SaveBar";
import { SignInScreen, useForgetSignInMarkers } from "@/components/SignIn";
import {
  PLATFORM_SIGN_IN_PATH,
  type PlatformSession,
  platformSignOut,
} from "@/lib/platform";
import { CellPage } from "@/platform/CellPage";
import { CellsPage } from "@/platform/CellsPage";
import { ENVIRONMENT_LABELS } from "@/platform/labels";

/** Not signed in on the platform host: the same automatic sign-in as the backoffice (D47). */
export function PlatformSignIn(): ReactElement {
  return <SignInScreen title="Plattform" path={PLATFORM_SIGN_IN_PATH} />;
}

/**
 * Shell of the platform administration.
 *
 * Environment and version stand in the header because this surface migrates databases:
 * whoever is about to press the button has to see which deployment it acts on.
 */
export function PlatformApp({
  session,
}: {
  readonly session: PlatformSession;
}): ReactElement {
  useForgetSignInMarkers(true);
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
        href="#inhalt"
      >
        Zum Inhalt springen
      </a>

      <header className="border-b border-border">
        <div className="mx-auto flex max-w-5xl flex-wrap items-baseline gap-x-2 px-4 py-5 text-xs text-muted-foreground">
          <p className="text-lg font-bold text-foreground">
            Plattform
          </p>
          <span>
            · {ENVIRONMENT_LABELS[session.environment]} · Version{" "}
            {session.version} ·
          </span>
          <button
            type="button"
            className="underline hover:text-foreground"
            onClick={() => {
              void platformSignOut();
            }}
          >
            Abmelden
          </button>
        </div>
      </header>

      <main id="inhalt" className="mx-auto max-w-5xl px-4 py-6">
        {/* The same save bar as in the backoffice (22.10.2026). */}
        <SaveBarProvider>
          <Routes>
            <Route path="/" element={<CellsPage />} />
            <Route
              path="/zellen/:slug"
              element={<CellPage seedAvailable={session.seed_available} />}
            />
            <Route
              path="*"
              element={
                <>
                  <PageHeading title="Seite nicht gefunden" />
                  <Link className="text-sm underline underline-offset-4" to="/">
                    Zellen
                  </Link>
                </>
              }
            />
          </Routes>
        </SaveBarProvider>
      </main>
    </div>
  );
}
